import { useEffect, useState } from 'react';
import { api } from '../api/client';

const STATUT_COLOR = { COMPLET: 'green', PARTIELLE: 'gold', NON_SAISIE: 'red', EN_ATTENTE: 'gray' };
const STATUT_LABEL = { COMPLET: 'Complet', PARTIELLE: 'Partielle', NON_SAISIE: 'Non saisie', EN_ATTENTE: 'En attente' };

function Bar({ value, max = 100, color = '#6366f1' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, minWidth: 36, textAlign: 'right' }}>{value != null ? value.toFixed(1) : '—'}</span>
    </div>
  );
}

function successColor(rate) {
  if (rate == null) return 'var(--muted)';
  if (rate >= 75) return 'var(--success, #16a34a)';
  if (rate >= 50) return 'var(--warning, #ca8a04)';
  return 'var(--danger, #dc2626)';
}

// ── Onglet Suivi saisie ───────────────────────────────────────────────────────
function TabSaisie({ periods }) {
  const [rows, setRows]       = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true); setError('');
    api.get(`/evaluations/rapport-saisie${periodId ? `?periodId=${periodId}` : ''}`)
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [periodId]);

  const totalEvals      = rows.reduce((a, r) => a + r.total, 0);
  const totalComplets   = rows.reduce((a, r) => a + r.complets, 0);
  const totalNonSaisies = rows.reduce((a, r) => a + r.nonSaisies, 0);
  const tauxGlobal      = totalEvals > 0 ? Math.round((totalComplets / totalEvals) * 100) : 0;
  const nbAlertes       = rows.filter((r) => r.nonSaisies > 0).length;

  function toggle(id) { setExpanded((p) => ({ ...p, [id]: !p[id] })); }

  return (
    <div>
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <div className="kpi"><span>Évaluations suivies</span><strong>{totalEvals}</strong></div>
        <div className={`kpi ${tauxGlobal < 80 ? 'gold' : ''}`}><span>Taux de saisie global</span><strong>{tauxGlobal}%</strong></div>
        <div className={`kpi ${totalNonSaisies > 0 ? 'gold' : ''}`}><span>Saisies manquantes</span><strong>{totalNonSaisies}</strong></div>
        <div className={`kpi ${nbAlertes > 0 ? 'gold' : ''}`}><span>Enseignants en alerte</span><strong>{nbAlertes}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Suivi par enseignant</h2>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} style={{ width: 200 }}>
            <option value="">Toutes les périodes</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="panel-body">
          {error   && <div className="form-error">{error}</div>}
          {loading ? <div className="loading">Chargement…</div>
          : rows.length === 0 ? <div className="empty">Aucune donnée.</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.map(({ teacher, evaluations, total, complets, nonSaisies, taux }) => {
                const isOpen   = !!expanded[teacher.id];
                const hasAlert = nonSaisies > 0;
                const tauxColor = taux >= 80 ? 'var(--success,#16a34a)' : taux >= 50 ? 'var(--warning,#ca8a04)' : 'var(--danger,#dc2626)';
                return (
                  <div key={teacher.id} style={{ border: `1px solid ${hasAlert ? 'var(--danger,#dc2626)' : 'var(--line)'}`, borderRadius: 8, overflow: 'hidden' }}>
                    <button onClick={() => toggle(teacher.id)} style={{ width: '100%', border: 'none', cursor: 'pointer', padding: '10px 16px', background: hasAlert ? 'var(--danger-soft,#fef2f2)' : 'var(--surface)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <b>{teacher.lastName} {teacher.firstName}</b>
                        {hasAlert && <span className="badge red">{nonSaisies} non saisie{nonSaisies > 1 ? 's' : ''}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <span style={{ fontSize: '0.83rem', color: 'var(--muted)' }}>{complets}/{total} complètes</span>
                        <b style={{ color: tauxColor, minWidth: 38 }}>{taux}%</b>
                        <span style={{ fontSize: 9, opacity: .5 }}>{isOpen ? '▼' : '▶'}</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="table-wrap" style={{ borderTop: '1px solid var(--line)' }}>
                        <table>
                          <thead>
                            <tr><th>Date</th><th>Titre</th><th>Classe</th><th>Matière</th><th className="num">Saisies</th><th className="num">Taux</th><th>Statut</th></tr>
                          </thead>
                          <tbody>
                            {evaluations.map((ev) => (
                              <tr key={ev.id} style={ev.statut === 'NON_SAISIE' ? { background: 'var(--danger-soft,#fef2f2)' } : undefined}>
                                <td className="mono">{new Date(ev.date).toLocaleDateString('fr-FR')}</td>
                                <td>{ev.title}</td><td>{ev.class}</td><td>{ev.subject}</td>
                                <td className="num">{ev.saisies} / {ev.effectif}</td>
                                <td className="num"><b>{ev.rate}%</b></td>
                                <td><span className={`badge ${STATUT_COLOR[ev.statut] || 'gray'}`}>{STATUT_LABEL[ev.statut] || ev.statut}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Onglet Performance ────────────────────────────────────────────────────────
function TabPerformance({ periods, classes }) {
  const [classId, setClassId]       = useState('');
  const [periodId, setPeriodId]     = useState('');
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (!classId || !periodId) { setData(null); return; }
    setLoading(true); setError('');
    api.get(`/evaluations/report?classId=${classId}&periodId=${periodId}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [classId, periodId]);

  const maxAvg = data ? Math.max(...data.subjects.map((s) => s.moyenne ?? 0), 1) : 20;

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <select value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 220 }}>
          <option value="">— Classe —</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} style={{ width: 220 }}>
          <option value="">— Période —</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {!classId || !periodId
        ? <div className="empty">Sélectionnez une classe et une période.</div>
        : error   ? <div className="form-error">{error}</div>
        : loading ? <div className="loading">Chargement…</div>
        : !data   ? null
        : (
          <>
            {/* KPIs */}
            <div className="kpi-grid" style={{ marginBottom: 20 }}>
              <div className="kpi">
                <span>Classe</span>
                <strong>{data.class}</strong>
              </div>
              <div className="kpi">
                <span>Effectif</span>
                <strong>{data.effectif}</strong>
              </div>
              <div className={`kpi ${(data.classAverage ?? 0) < data.passMark ? 'gold' : ''}`}>
                <span>Moyenne générale</span>
                <strong>{data.classAverage != null ? data.classAverage.toFixed(2) : '—'} / 20</strong>
              </div>
              <div className="kpi">
                <span>Matières évaluées</span>
                <strong>{data.subjects.length}</strong>
              </div>
            </div>

            {/* Tableau par matière */}
            <div className="panel">
              <div className="panel-head"><h2>Résultats par matière</h2></div>
              <div className="panel-body">
                {data.subjects.length === 0
                  ? <div className="empty">Aucune évaluation saisie pour cette sélection.</div>
                  : (
                    <table>
                      <thead>
                        <tr>
                          <th>Matière</th>
                          <th className="num">Élèves notés</th>
                          <th className="num">Min</th>
                          <th className="num">Max</th>
                          <th style={{ minWidth: 160 }}>Moyenne /20</th>
                          <th className="num">Taux de réussite</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.subjects.map((s) => (
                          <tr key={s.subjectId}>
                            <td><b>{s.name}</b></td>
                            <td className="num">{s.noted}</td>
                            <td className="num mono" style={{ color: 'var(--danger,#dc2626)' }}>{s.min != null ? s.min.toFixed(1) : '—'}</td>
                            <td className="num mono" style={{ color: 'var(--success,#16a34a)' }}>{s.max != null ? s.max.toFixed(1) : '—'}</td>
                            <td style={{ minWidth: 160 }}>
                              <Bar value={s.moyenne} max={20} color={successColor(s.successRate)} />
                            </td>
                            <td className="num">
                              <b style={{ color: successColor(s.successRate) }}>
                                {s.successRate != null ? `${s.successRate}%` : '—'}
                              </b>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          </>
        )
      }
    </div>
  );
}

// ── Onglet Alertes ────────────────────────────────────────────────────────────
function TabAlertes({ periods, classes }) {
  const [classId, setClassId]   = useState('');
  const [periodId, setPeriodId] = useState('');
  const [seuil, setSeuil]       = useState(10);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!classId || !periodId) { setData(null); return; }
    setLoading(true); setError('');
    api.get(`/evaluations/alertes?classId=${classId}&periodId=${periodId}&threshold=${seuil}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [classId, periodId, seuil]);

  function toggle(id) { setExpanded((p) => ({ ...p, [id]: !p[id] })); }

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <select value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 220 }}>
          <option value="">— Classe —</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} style={{ width: 220 }}>
          <option value="">— Période —</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
          Seuil :
          <input
            type="number" min={0} max={20} step={0.5} value={seuil}
            onChange={(e) => setSeuil(Number(e.target.value))}
            style={{ width: 64, padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 6 }}
          />
          <span>/ 20</span>
        </label>
      </div>

      {!classId || !periodId
        ? <div className="empty">Sélectionnez une classe et une période.</div>
        : error   ? <div className="form-error">{error}</div>
        : loading ? <div className="loading">Chargement…</div>
        : !data   ? null
        : (
          <>
            {/* KPIs */}
            <div className="kpi-grid" style={{ marginBottom: 20 }}>
              <div className="kpi"><span>Classe</span><strong>{data.class}</strong></div>
              <div className="kpi"><span>Élèves évalués</span><strong>{data.total} / {data.effectif}</strong></div>
              <div className={`kpi ${data.nbAlertes > 0 ? 'red' : ''}`}>
                <span>Élèves en difficulté</span>
                <strong>{data.nbAlertes}</strong>
              </div>
              <div className={`kpi ${data.tauxDifficulte > 30 ? 'red' : data.tauxDifficulte > 15 ? 'gold' : ''}`}>
                <span>Taux de difficulté</span>
                <strong>{data.tauxDifficulte}%</strong>
              </div>
            </div>

            {data.students.length === 0
              ? <div className="empty" style={{ color: 'var(--success,#16a34a)', fontWeight: 600 }}>
                  ✓ Aucun élève sous le seuil de {data.seuil}/20.
                </div>
              : (
                <div className="panel">
                  <div className="panel-head">
                    <h2>Élèves sous {data.seuil}/20 — du plus en difficulté au moins</h2>
                  </div>
                  <div className="panel-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {data.students.map(({ student, moyenneGenerale, subjects }) => {
                        const isOpen = !!expanded[student.id];
                        const avg = moyenneGenerale ?? 0;
                        const color = avg < data.seuil * 0.6 ? 'var(--danger,#dc2626)' : 'var(--warning,#ca8a04)';
                        return (
                          <div key={student.id} style={{ border: `1px solid ${color}`, borderRadius: 8, overflow: 'hidden' }}>
                            <button onClick={() => toggle(student.id)} style={{
                              width: '100%', border: 'none', cursor: 'pointer', padding: '10px 16px',
                              background: avg < data.seuil * 0.6 ? 'var(--danger-soft,#fef2f2)' : 'var(--warning-soft,#fffbeb)',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}>
                              <span><b>{student.lastName} {student.firstName}</b>{student.matricule && <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 8 }}>{student.matricule}</span>}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <b style={{ color, fontSize: 16 }}>{avg.toFixed(2)} / 20</b>
                                <span style={{ fontSize: 9, opacity: .5 }}>{isOpen ? '▼' : '▶'}</span>
                              </div>
                            </button>
                            {isOpen && (
                              <div className="table-wrap" style={{ borderTop: `1px solid ${color}` }}>
                                <table>
                                  <thead><tr><th>Matière</th><th className="num">Moyenne /20</th></tr></thead>
                                  <tbody>
                                    {subjects.map((s) => (
                                      <tr key={s.subjectId}>
                                        <td>{s.name}</td>
                                        <td className="num">
                                          <b style={{ color: s.moyenne != null && s.moyenne < data.seuil ? 'var(--danger,#dc2626)' : undefined }}>
                                            {s.moyenne != null ? s.moyenne.toFixed(2) : '—'}
                                          </b>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )
            }
          </>
        )
      }
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function PedagogieDashboard() {
  const [tab, setTab]         = useState('saisie');
  const [periods, setPeriods] = useState([]);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    api.get('/catalog/periods').then(setPeriods).catch(() => {});
    api.get('/catalog/classes').then(setClasses).catch(() => {});
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', gap: 8, borderBottom: '1px solid var(--line)', paddingBottom: 0 }}>
        {[
          { key: 'saisie',       label: 'Suivi des saisies' },
          { key: 'performance',  label: 'Performance par classe' },
          { key: 'alertes',      label: '⚠ Alertes & seuils' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 18px', border: 'none', cursor: 'pointer', fontWeight: 600,
            borderBottom: tab === t.key ? '2px solid var(--primary,#6366f1)' : '2px solid transparent',
            color: tab === t.key ? 'var(--primary,#6366f1)' : 'var(--muted)',
            background: 'none',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'saisie'      && <TabSaisie periods={periods} />}
      {tab === 'performance' && <TabPerformance periods={periods} classes={classes} />}
      {tab === 'alertes'     && <TabAlertes periods={periods} classes={classes} />}
    </div>
  );
}
